import { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import {
  fetchActualHours,
  selectActualHoursAreLoading,
} from 'state/ActualHours/actualHoursSlice';
import { selectUserId } from 'state/User/userSlice';
import useModal from 'Hooks/useModal';
import useUpdateActualHours from './hooks/useUpdateActualHours';
import SkeletonLoader from './TimeEntries/TimeEntriesSkeletonLoader';
import TimeEntriesList from './TimeEntries/TimeEntriesList';
import AddHoursForm from './AddHoursForm/AddHoursForm';
import ActionsNotificationsList from './ActionsNotificationsList/ActionsNotificationsList';
import UnsavedChangesModal from 'Organisms/UnsavedChangesModal/UnsavedChangesModal';

type TaskActualHoursModalProps = {
  taskId: string;
  canAddActualHours: boolean;
  shouldDisplayModal: boolean;
  toggleDisplayModal: () => void;
  refresh?: () => void;
};

const TaskActualHoursModal = ({
  taskId,
  canAddActualHours = true,
  shouldDisplayModal = false,
  toggleDisplayModal,
  refresh,
}: TaskActualHoursModalProps) => {
  const dispatch = useDispatch();
  const isLoadingHours = useSelector(selectActualHoursAreLoading);
  const currentUserId = useSelector(selectUserId);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const { Modal, modalProps, closeModal } = useModal();

  const handleCloseModalWithoutRefresh = useCallback(() => {
    toggleDisplayModal();
    closeModal();
  }, [closeModal, toggleDisplayModal]);

  const handleCloseModalWithRefresh = useCallback(() => {
    refresh?.();
    toggleDisplayModal();
    closeModal();
  }, [closeModal, toggleDisplayModal, refresh]);

  const {
    canSave,
    updateNewActualHoursEntries,
    handleSaveActualHours,
    handleDeletedHours,
    reset,
  } = useUpdateActualHours({
    taskId,
    onSuccessCallback: handleCloseModalWithRefresh,
  });

  const validateIfCanCloseModal = useCallback(() => {
    if (canSave) {
      setShowUnsavedChangesModal(true);
    } else {
      handleCloseModalWithoutRefresh();
    }
  }, [canSave, handleCloseModalWithoutRefresh]);

  useEffect(() => {
    if (shouldDisplayModal) {
      dispatch(fetchActualHours({ taskId }));
    } else {
      reset();
    }
  }, [dispatch, taskId, shouldDisplayModal, reset]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('SAVE'),
        variant: 'primary',
        onClick: handleSaveActualHours,
        disabled: !canSave,
        'data-testid': 'save-actuals-button',
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModalWithoutRefresh,
        'data-testid': 'cancel-actuals-button',
      },
    ],
    [handleCloseModalWithoutRefresh, canSave, handleSaveActualHours]
  );

  return (
    <>
      <Modal
        {...modalProps}
        title={intl.get('TASK_ACTUAL_HOURS.TITLE')}
        titleIcon={{
          name: 'time-outline',
          className: 'text-neutral font-medium',
        }}
        aria-label={intl.get('TASK_ACTUAL_HOURS.TITLE')}
        size='medium'
        actionButtons={actionButtons}
        closeModal={validateIfCanCloseModal}
        isOpen={shouldDisplayModal}
        overlayClassName='z-50'
      >
        {canAddActualHours && (
          <AddHoursForm
            userId={currentUserId!}
            updateNewActualHoursEntries={updateNewActualHoursEntries}
          />
        )}
        {isLoadingHours && <SkeletonLoader />}
        {!isLoadingHours && (
          <TimeEntriesList
            isViewOnly={!canAddActualHours}
            onDeleteHours={handleDeletedHours}
          />
        )}
      </Modal>
      <ActionsNotificationsList />
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        setIsOpen={setShowUnsavedChangesModal}
        onConfirm={handleCloseModalWithoutRefresh}
        overlayClassName='z-50'
      />
    </>
  );
};

export default TaskActualHoursModal;
